import { dailyKommentarVorspann, datumLesbar } from '@netcon-work/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import { trpc } from '../../lib/trpc'
import { useAufgabenDieserWelt, useErwaehnbarePersonen } from '../../lib/welt'
import { useWirksameTermine } from '../../lib/termine'
import { AufgabenVerweis, AufgabeProvider } from '../AufgabeKontext'
import { MarkdownFeld } from '../MarkdownFeld'
import { Button } from '../ui/primitives'
import { hintergrundSchliesst } from '../ui/hintergrund'
import { rundeFuer, type RundenZeile } from './daily-runde'

/**
 * **Das Daily** – die Morgenbesprechung, als Ansicht
 * ([Z-18](../../../../../docs/phasen/zusatz-18-daily.md)).
 *
 * Nils: *„Das Daily findet jeden Morgen um 9:15 statt."* Der Knopf steht im Leitstand oben
 * rechts; was er öffnet, ist ein **Protokoll** und kein Fenster – es überlebt das Schließen und
 * ist später wieder aufrufbar.
 *
 * Die Gestaltung folgt dem abgenommenen Mockup (`mockup-daily.html`, 18.09.2026).
 */

type Daily = Awaited<ReturnType<typeof trpc.daily.heute.mutate>>
type Eintrag = Daily['eintraege'][number]

/** **Der Knopf im Leitstand.** Er öffnet das heutige Daily – oder legt es an. */
export function DailyKnopf() {
  const [offen, setOffen] = useState<Daily | null>(null)
  const oeffnen = useMutation({
    mutationFn: () => trpc.daily.heute.mutate(),
    onSuccess: setOffen,
  })

  return (
    <>
      <button
        type="button"
        onClick={() => oeffnen.mutate()}
        disabled={oeffnen.isPending}
        className="flex cursor-pointer items-center gap-2 rounded-[9px] bg-accent px-3.5 py-2 text-[13px] font-semibold text-white hover:brightness-105 disabled:opacity-60"
      >
        <span aria-hidden>🕘</span>
        Daily
        <span className="border-l border-white/30 pl-2 text-xs font-medium opacity-80">
          {/**
           * **Nur die Uhrzeit.** Im Mockup stand „Fr 9:15" – als Beispieltag. Abgeschrieben
           * behauptete der Knopf an jedem Montag, es sei Freitag. Das Daily ist täglich; der
           * Wochentag trägt hier nichts bei, die Uhrzeit schon.
           */}
          {oeffnen.isPending ? 'öffnet …' : '9:15'}
        </span>
      </button>
      {offen && (
        <AufgabeProvider>
          <DailyFenster daily={offen} setzen={setOffen} schliessen={() => setOffen(null)} />
        </AufgabeProvider>
      )}
    </>
  )
}

function DailyFenster({
  daily,
  setzen,
  schliessen,
}: {
  daily: Daily
  setzen: (d: Daily) => void
  schliessen: () => void
}) {
  const [archivOffen, setArchivOffen] = useState(false)
  const { aufgaben } = useAufgabenDieserWelt()
  /** Z-20: der wirksame Termin – „Als Nächstes" sortiert wie der Vorlauf. */
  const termine = useWirksameTermine()
  /**
   * **Seit wann jede Aufgabe in ihrem Status steht** – aus den Statuswechseln, nicht aus
   * `updatedAt` (20.10.2026). Jede Minute neu: Was während der Runde fertig wird, soll in ihr
   * auftauchen.
   */
  const { data: seit } = useQuery({
    queryKey: ['daily', 'seit'],
    queryFn: () => trpc.daily.seit.query(),
    refetchInterval: 60_000,
  })
  const erwaehnbar = useErwaehnbarePersonen()
  /** **Abgelegt heißt: nur lesen.** Ein Protokoll, das sich später ändern lässt, ist keins. */
  const abgelegt = daily.beendetAm !== null

  const neuLaden = useCallback(async () => {
    setzen(await trpc.daily.eines.query({ id: daily.id }))
  }, [daily.id, setzen])

  /**
   * **Wer gerade dran ist.** Ein Daily geht Person für Person – deshalb ist das ein Zustand und
   * keine Hervorhebung beim Überfahren. Anwesende zuerst; wer nicht da ist, wird übersprungen.
   */
  const anwesende = useMemo(() => daily.teilnehmer.filter((t) => t.anwesend), [daily.teilnehmer])
  const [dran, setDran] = useState<string | null>(null)
  useEffect(() => {
    setDran((v) => v ?? anwesende[0]?.personId ?? null)
  }, [anwesende])

  const weiter = useCallback(() => {
    const i = anwesende.findIndex((t) => t.personId === dran)
    const naechster = anwesende[(i + 1) % Math.max(anwesende.length, 1)]
    if (naechster) setDran(naechster.personId)
  }, [anwesende, dran])

  useEffect(() => {
    function taste(e: KeyboardEvent) {
      if (e.key === 'Escape') schliessen()
      const imFeld = /^(TEXTAREA|INPUT)$/.test(document.activeElement?.tagName ?? '')
      if (e.key === 'ArrowDown' && !imFeld) {
        e.preventDefault()
        weiter()
      }
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [schliessen, weiter])

  const jetzt = Date.now()
  const markierungen = daily.eintraege.filter((e) => e.art === 'markierung')
  const notizen = daily.eintraege.filter((e) => e.art === 'notiz')
  const werdenKommentar = daily.eintraege.filter((e) => e.taskId && !e.kommentarId).length

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-40 flex items-start justify-center bg-[rgb(23_22_20/0.32)] p-3 pt-[3vh] sm:p-6 sm:pt-[5vh]"
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label={`Daily vom ${datumLesbar(daily.datum)}`}
    >
      <div className="flex max-h-[min(94vh,100%)] w-full min-w-0 max-w-[1320px] flex-col rounded-card border border-line bg-page shadow-card">
        {/* ── Kopf ── */}
        <div className="flex flex-wrap items-center gap-3 rounded-t-card border-b border-line bg-surface px-5 py-3">
          <div>
            <button
              type="button"
              onClick={() => setArchivOffen((v) => !v)}
              aria-expanded={archivOffen}
              className="flex cursor-pointer items-center gap-2 text-base font-bold tracking-[-0.2px]"
            >
              Daily · {datumLesbar(daily.datum)}
              <span className="text-[11px] text-muted">▾</span>
            </button>
            <div className="text-xs text-muted">
              {anwesende.length} von {daily.teilnehmer.length} da
              {dran && !abgelegt && (
                <>
                  {' · Runde bei '}
                  <b className="font-semibold text-ink2">
                    {daily.teilnehmer.find((t) => t.personId === dran)?.name}
                  </b>
                </>
              )}
            </div>
          </div>
          {abgelegt ? (
            <span className="rounded-full bg-[#f0efec] px-2.5 py-0.5 text-[11.5px] font-semibold text-ink2">
              ✓ abgelegt · nur lesen
            </span>
          ) : (
            <span className="rounded-full bg-[#fdf1f1] px-2.5 py-0.5 text-[11.5px] font-semibold text-[#d03b3b]">
              läuft
            </span>
          )}
          <button
            type="button"
            onClick={schliessen}
            className="ml-auto cursor-pointer rounded-lg px-2 py-1 text-ink2 hover:bg-[#f3f2ef]"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        {archivOffen && <Archiv aktuell={daily.id} waehlen={setzen} />}

        <Teilnehmerleiste daily={daily} abgelegt={abgelegt} neuLaden={neuLaden} />

        {/* ── Runde und Notizen ── */}
        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[1fr_352px]">
          <div className="min-w-0 overflow-y-auto px-4 py-3.5">
            {daily.teilnehmer.map((t) => (
              <Personenkarte
                key={t.personId}
                person={t}
                runde={rundeFuer(aufgaben, t.personId, jetzt, termine, seit)}
                dran={t.personId === dran}
                abgelegt={abgelegt}
                waehlen={() => setDran(t.personId)}
                markieren={async (zeile) => {
                  await trpc.daily.eintragAnlegen.mutate({
                    dailyId: daily.id,
                    art: 'markierung',
                    /**
                     * **Ohne Text.** Der Titel steht schon an der Aufgabe; ihn als Satz zu
                     * wiederholen ergäbe den Kommentar „Aus dem Daily vom …: <derselbe Titel>".
                     * Was gesagt wurde, kommt gleich daneben hinein.
                     */
                    body: '',
                    taskId: zeile.id,
                  })
                  await neuLaden()
                }}
                personMarkieren={async () => {
                  await trpc.daily.eintragAnlegen.mutate({
                    dailyId: daily.id,
                    art: 'markierung',
                    body: '',
                    personId: t.personId,
                  })
                  await neuLaden()
                }}
                durch={weiter}
              />
            ))}
          </div>

          <div className="min-w-0 overflow-y-auto border-line bg-surface px-4 py-3.5 lg:border-l">
            <Spalte titel={`Markierungen · ${markierungen.length}`}>
              {markierungen.length === 0 && (
                <p className="text-xs text-muted italic">
                  Noch nichts markiert. Das ⚑ an einer Zeile setzt hier einen Punkt.
                </p>
              )}
              {markierungen.map((e) => (
                <Markierung
                  key={e.id}
                  eintrag={e}
                  abgelegt={abgelegt}
                  neuLaden={neuLaden}
                  entfernen={async () => {
                    await trpc.daily.eintragLoeschen.mutate({ id: e.id })
                    await neuLaden()
                  }}
                />
              ))}
            </Spalte>

            <Spalte titel="Notizen zum Daily">
              {notizen.map((e) => (
                <div key={e.id} className="text-[12.5px] leading-relaxed">
                  <Markdown text={e.body} />
                </div>
              ))}
              {!abgelegt && (
                <Schreibfeld
                  erwaehnbar={erwaehnbar}
                  absenden={async (text) => {
                    await trpc.daily.eintragAnlegen.mutate({
                      dailyId: daily.id,
                      art: 'notiz',
                      body: text,
                    })
                    await neuLaden()
                  }}
                />
              )}
              <p className="mt-2 text-[11.5px] leading-relaxed text-muted">
                Punkte mit Aufgabenbezug landen dort als Kommentar:{' '}
                <span className="rounded bg-[#f3f2ef] px-1 text-ink2 italic">
                  {dailyKommentarVorspann(daily.datum)}…
                </span>
              </p>
            </Spalte>
          </div>
        </div>

        {/* ── Fuß ── */}
        <div className="flex flex-wrap items-center gap-2.5 rounded-b-card border-t border-line bg-surface px-5 py-2.5">
          <span className="text-[11px] text-muted">
            Runde: <kbd className="rounded border border-line2 bg-[#f0efec] px-1">↓</kbd> nächste
            Person · <kbd className="rounded border border-line2 bg-[#f0efec] px-1">Esc</kbd>{' '}
            schließen
          </span>
          <span className="ml-auto text-[11px] text-muted">
            {abgelegt
              ? 'Abgelegt – hier wird nichts mehr geändert.'
              : werdenKommentar === 0
                ? 'Nichts mit Aufgabenbezug – es wird kein Kommentar eingetragen'
                : `${werdenKommentar} ${werdenKommentar === 1 ? 'Punkt wird' : 'Punkte werden'} als Kommentar eingetragen`}
          </span>
          <Button onClick={schliessen}>{abgelegt ? 'Schließen' : 'Später weiter'}</Button>
          {!abgelegt && <Beenden dailyId={daily.id} fertig={neuLaden} />}
        </div>
      </div>
    </div>
  )
}

function Beenden({ dailyId, fertig }: { dailyId: string; fertig: () => Promise<void> }) {
  const beenden = useMutation({
    mutationFn: () => trpc.daily.beenden.mutate({ id: dailyId }),
    onSuccess: fertig,
  })
  return (
    <Button variant="primary" disabled={beenden.isPending} onClick={() => beenden.mutate()}>
      {beenden.isPending ? 'Legt ab …' : 'Daily beenden & ablegen'}
    </Button>
  )
}

function Spalte({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <section className="mb-5 flex flex-col gap-2.5 last:mb-0">
      <h4 className="text-[11px] font-bold tracking-[0.8px] text-muted uppercase">{titel}</h4>
      {children}
    </section>
  )
}

function Markierung({
  eintrag,
  abgelegt,
  entfernen,
  neuLaden,
}: {
  eintrag: Eintrag
  abgelegt: boolean
  entfernen: () => Promise<void>
  neuLaden: () => Promise<void>
}) {
  const [entwurf, setEntwurf] = useState(eintrag.body)
  const festgehalten = eintrag.body

  /**
   * **Der Satz wird beim Verlassen des Feldes gespeichert**, nicht bei jedem Zeichen. Im Daily
   * tippt man, während jemand redet; ein Aufruf je Tastendruck wäre Lärm auf der Leitung und
   * ein halber Satz im Protokoll, wenn das Fenster dazwischen zugeht.
   */
  async function sichern() {
    if (entwurf.trim() === festgehalten.trim()) return
    await trpc.daily.eintragAendern.mutate({ id: eintrag.id, body: entwurf })
    await neuLaden()
  }

  return (
    <div className="rounded-[9px] border border-[#eccf8a] bg-[#fdf3dc] px-2.5 py-2 text-xs">
      <div className="flex items-start gap-2">
        <b className="min-w-0 font-semibold">{eintrag.aufgabeTitel ?? 'Person'}</b>
        {!abgelegt && !eintrag.kommentarId && (
          <button
            type="button"
            onClick={() => void entfernen()}
            className="ml-auto flex-none cursor-pointer text-[#8a6100] opacity-60 hover:opacity-100"
            aria-label="Punkt entfernen"
          >
            ✕
          </button>
        )}
      </div>

      {abgelegt || eintrag.kommentarId ? (
        <p className="mt-0.5 text-[#a04a12]">{eintrag.body || 'Im Daily besprochen.'}</p>
      ) : (
        <input
          value={entwurf}
          onChange={(e) => setEntwurf(e.target.value)}
          onBlur={() => void sichern()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur()
          }}
          placeholder="Was wurde dazu gesagt?"
          aria-label={`Notiz zu ${eintrag.aufgabeTitel ?? 'diesem Punkt'}`}
          className="mt-1 w-full rounded-md border border-[#eccf8a] bg-surface px-1.5 py-1 text-xs outline-none focus:border-[#a04a12]"
        />
      )}

      <span className="mt-1 block text-[10.5px] text-muted">
        {eintrag.kommentarId
          ? '✓ steht als Kommentar an der Aufgabe'
          : eintrag.taskId
            ? '→ Kommentar an der Aufgabe'
            : 'bleibt im Protokoll'}
      </span>
    </div>
  )
}

function Schreibfeld({
  erwaehnbar,
  absenden,
}: {
  erwaehnbar: NonNullable<Parameters<typeof MarkdownFeld>[0]['erwaehnbar']>
  absenden: (text: string) => Promise<void>
}) {
  const [entwurf, setEntwurf] = useState('')
  const schicken = useMutation({
    mutationFn: () => absenden(entwurf),
    onSuccess: () => setEntwurf(''),
  })
  return (
    <div>
      <MarkdownFeld
        wert={entwurf}
        setzen={setEntwurf}
        zeilen={3}
        platzhalter="Notiz … @ spricht jemanden an"
        beiAbsenden={() => {
          if (entwurf.trim()) schicken.mutate()
        }}
        erwaehnbar={erwaehnbar}
      />
      <div className="mt-1.5 flex items-center gap-2">
        <span className="text-[11px] text-muted">@ Person</span>
        <Button
          variant="primary"
          className="ml-auto"
          disabled={!entwurf.trim() || schicken.isPending}
          onClick={() => schicken.mutate()}
        >
          Notieren
        </Button>
      </div>
    </div>
  )
}

function Teilnehmerleiste({
  daily,
  abgelegt,
  neuLaden,
}: {
  daily: Daily
  abgelegt: boolean
  neuLaden: () => Promise<void>
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-[18px] py-2.5">
      <span className="mr-1 text-[10.5px] font-bold tracking-[0.8px] text-muted uppercase">
        Teilnehmer
      </span>
      {daily.teilnehmer.map((t) => (
        <button
          key={t.personId}
          type="button"
          disabled={abgelegt}
          onClick={async () => {
            await trpc.daily.teilnehmerSetzen.mutate({
              dailyId: daily.id,
              personId: t.personId,
              anwesend: !t.anwesend,
            })
            await neuLaden()
          }}
          className={cn(
            'flex cursor-pointer items-center gap-1.5 rounded-full border border-line2 px-3 py-1 text-[12.5px] font-semibold disabled:cursor-default',
            t.anwesend ? 'bg-[#f7f6f3] text-ink' : 'text-muted',
          )}
        >
          {t.name}
          {!t.anwesend && t.grund && (
            <span className="rounded-full bg-[#fdf3dc] px-1.5 text-[10.5px] font-medium text-[#a04a12]">
              {t.grund}
            </span>
          )}
        </button>
      ))}
      <span className="ml-auto text-xs text-muted">
        Abwesenheiten kommen aus der Urlaubsplanung – hier übersteuerbar
      </span>
    </div>
  )
}

function Archiv({ aktuell, waehlen }: { aktuell: string; waehlen: (d: Daily) => void }) {
  const [liste, setListe] = useState<Awaited<ReturnType<typeof trpc.daily.liste.query>>>([])
  useEffect(() => {
    void trpc.daily.liste.query({ limit: 20 }).then(setListe)
  }, [])

  return (
    <div className="border-b border-line bg-surface px-3.5 pt-1.5 pb-2.5">
      {liste.length === 0 && (
        <p className="px-2.5 py-2 text-xs text-muted">Noch keine früheren Dailys.</p>
      )}
      {liste.map((d) => (
        <button
          key={d.id}
          type="button"
          disabled={d.id === aktuell}
          onClick={async () => waehlen(await trpc.daily.eines.query({ id: d.id }))}
          className="flex w-full cursor-pointer items-center gap-3.5 rounded-[9px] px-2.5 py-1.5 text-left text-[12.5px] hover:bg-[#f3f2ef] disabled:cursor-default disabled:opacity-50"
        >
          <span className="w-[86px] flex-none font-bold tabular-nums">{datumLesbar(d.datum)}</span>
          <span className="w-24 flex-none text-ink2">
            {d.anwesend} von {d.teilnehmer} da
          </span>
          <span className="text-muted">
            {d.eintraege} {d.eintraege === 1 ? 'Punkt' : 'Punkte'}
            {d.beendetAm ? '' : ' · läuft noch'}
          </span>
        </button>
      ))}
    </div>
  )
}

function Personenkarte({
  person,
  runde,
  dran,
  abgelegt,
  waehlen,
  markieren,
  personMarkieren,
  durch,
}: {
  person: Daily['teilnehmer'][number]
  runde: ReturnType<typeof rundeFuer>
  dran: boolean
  abgelegt: boolean
  waehlen: () => void
  /** `oeffneAufgabe` ist hier weg – jeder Titel ist seit E-371 ein Verweis. */
  markieren: (zeile: RundenZeile) => Promise<void>
  personMarkieren: () => Promise<void>
  durch: () => void
}) {
  return (
    <div
      className={cn(
        'mb-2.5 overflow-hidden rounded-xl border border-line border-l-[3px] bg-surface',
        dran ? 'border-l-accent shadow-card' : 'border-l-transparent',
        !person.anwesend && 'opacity-55',
      )}
    >
      <button
        type="button"
        onClick={waehlen}
        className="flex w-full cursor-pointer items-center gap-2.5 border-b border-line px-3.5 py-2.5 text-left"
      >
        <div>
          <div className="text-[13.5px] font-bold tracking-[-0.1px]">{person.name}</div>
          {!person.anwesend && (
            <div className="text-[11px] text-muted">
              nicht da{person.grund ? ` · ${person.grund}` : ''}
            </div>
          )}
        </div>
        {person.anwesend && !abgelegt && (
          <span className="ml-auto flex items-center gap-1.5">
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                void personMarkieren()
              }}
              onKeyDown={(e) => e.key === 'Enter' && void personMarkieren()}
              className="cursor-pointer rounded-lg border border-line2 px-2.5 py-1 text-xs font-semibold text-ink2"
            >
              ⚑ Person
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                durch()
              }}
              onKeyDown={(e) => e.key === 'Enter' && durch()}
              className="cursor-pointer rounded-lg bg-[#f3f2ef] px-2.5 py-1 text-xs font-semibold"
            >
              ✓ Durch
            </span>
          </span>
        )}
      </button>

      {person.anwesend && (
        <div className="grid grid-cols-1 sm:grid-cols-3">
          <Spur
            titel="Erledigt · 7 Tage"
            zeilen={runde.erledigt}
            zeichen="✓"
            ton="text-[#006300]"
            abgelegt={abgelegt}
            markieren={markieren}
          />
          <Spur
            titel="In Arbeit"
            zeilen={runde.inArbeit}
            zeichen="▸"
            ton="text-[#1c5cab]"
            abgelegt={abgelegt}
            markieren={markieren}
            zeigtTage
          />
          <Spur
            titel="Als Nächstes"
            zeilen={runde.naechstes}
            nummeriert
            ton="text-muted"
            abgelegt={abgelegt}
            markieren={markieren}
          />
        </div>
      )}

      {/**
       * **Wartet auf Abnahme – ein Streifen, keine vierte Spalte** (23.09.2026,
       * [E-371](../../../../../docs/ENTSCHEIDUNGEN.md#e-371)).
       *
       * Nils hatte eine vierte Kachel vorgeschlagen und nach Vorlage von drei Fassungen den
       * Streifen gewählt. Der Grund steht im Eintrag: Die Spur hat fast immer null oder eine
       * Zeile, eine vierte Spalte nähme dauerhaft ein Viertel der Breite – und die Titel
       * brechen in den drei vorhandenen schon heute um.
       *
       * **Er erscheint nur, wenn etwas wartet.** Ein leerer Streifen mit einem Gedankenstrich
       * wäre an jeder Karte eine Zeile, die nichts sagt; die drei Spuren zeigen ihr „–", weil
       * dort die Abwesenheit selbst eine Auskunft ist („nichts erledigt diese Woche").
       */}
      {person.anwesend && runde.abnahme.length > 0 && (
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1.5 border-t border-[#f0d9c6] bg-[#fdf1ea] px-3.5 py-2">
          <h4 className="flex flex-none items-baseline gap-1.5 text-[10.5px] font-bold tracking-[0.8px] text-warn uppercase">
            ⏳ Wartet auf Abnahme
            <b className="text-xs tabular-nums">{runde.abnahme.length}</b>
          </h4>
          {runde.abnahme.map((z) => (
            <div
              key={z.id}
              className="group flex items-start gap-1.5 rounded-md border border-[#f0d9c6] bg-white/55 px-1.5 py-0.5 text-[12.5px] hover:bg-white"
            >
              <AufgabenVerweis
                aufgabeId={z.id}
                className="min-w-0 cursor-pointer text-left hover:underline"
              >
                {z.titel}
                {/**
                 * **Der Abnehmer gehört in die Zeile.** „Wartet" allein beantwortet die Frage
                 * der Runde nicht – die lautet *wer nimmt das ab*, und die Antwort ist ein
                 * Name. Fehlt er, steht nichts da: Eine Aufgabe auf Review ohne Abnehmer ist
                 * ein Ablaufschritt, auf den niemand wartet (E-231).
                 */}
                {z.abnehmer && <span className="text-warn"> · bei {z.abnehmer}</span>}
                {z.tage !== null && <span className="text-muted"> · {z.tage} T</span>}
              </AufgabenVerweis>
              {!abgelegt && (
                <button
                  type="button"
                  onClick={() => void markieren(z)}
                  title="markieren"
                  className="flex-none cursor-pointer text-[#e0c3a6] group-hover:text-warn hover:!text-[#8a6100]"
                >
                  ⚑
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Spur({
  titel,
  zeilen,
  zeichen,
  nummeriert,
  ton,
  abgelegt,
  zeigtTage,
  markieren,
}: {
  titel: string
  zeilen: RundenZeile[]
  zeichen?: string
  nummeriert?: boolean
  ton: string
  abgelegt: boolean
  zeigtTage?: boolean
  /** `oeffnen` braucht die Spur nicht mehr – der Titel ist seit E-371 ein Verweis. */
  markieren: (zeile: RundenZeile) => Promise<void>
}) {
  return (
    <div className="min-w-0 px-3.5 py-2.5 sm:border-l sm:border-dashed sm:border-line sm:first:border-l-0">
      <h4
        className={cn(
          'mb-2 flex items-baseline gap-1.5 text-[10.5px] font-bold tracking-[0.8px] uppercase',
          ton,
        )}
      >
        {titel}
        <b className="text-xs tabular-nums">{zeilen.length}</b>
      </h4>
      {zeilen.length === 0 && <p className="px-1.5 text-xs text-muted italic">–</p>}
      {zeilen.map((z, i) => (
        <div
          key={z.id}
          className="group flex items-start gap-1.5 rounded-md px-1.5 py-1 text-[12.5px] hover:bg-[#f7f6f3]"
        >
          <span className="w-3.5 flex-none pt-px text-center text-[11px] text-muted tabular-nums">
            {nummeriert ? i + 1 : zeichen}
          </span>
          {/**
           * **Auch hier ein Verweis** (23.09.2026, E-371): Das Daily war die letzte Ansicht, in
           * der ein Aufgabentitel ein `<button>` war – Strg+Klick öffnete keinen Tab
           * ([E-368](../../../../../docs/ENTSCHEIDUNGEN.md#e-368)). Aufgefallen beim Bau des
           * Streifens, und zwar dem Wächter `lib/aufgabenverweis.test.ts`, nicht mir.
           */}
          <AufgabenVerweis
            aufgabeId={z.id}
            className="min-w-0 cursor-pointer text-left hover:underline"
          >
            {z.titel}
            {zeigtTage && z.tage !== null && <span className="text-muted"> · {z.tage} T</span>}
          </AufgabenVerweis>
          {!abgelegt && (
            <button
              type="button"
              onClick={() => void markieren(z)}
              title="markieren"
              className="ml-auto flex-none cursor-pointer text-line2 group-hover:text-muted hover:!text-[#8a6100]"
            >
              ⚑
            </button>
          )}
        </div>
      ))}
    </div>
  )
}
